// The slides editor's operations beyond the shared model (AB#177): slides (add from a layout,
// delete, move), z-order, align / distribute, copy / paste, and what the insert menu creates.
// Pure helpers read a SlideSnapshot; each mutation is ONE Y transaction under the caller's origin
// (LOCAL_ORIGIN by default, so it is one step of the member's own undo stack). These are web-only:
// the backend's equivalents are the AI's structural ops (insertSlide / deleteSlide / moveSlide) and
// write_slide, which the shared fixture already pins.
import * as Y from 'yjs'
import { descendantsOf, parentsOf, removeElements, translate } from './groups'
import { connectorEnds, unbindEnds } from './connectors'
import { deckAnchors } from './anchors'
import {
  LOCAL_ORIGIN,
  addElement,
  addSlide,
  buildElement,
  childrenOf,
  dropRunAnchors,
  elementsOf,
  newId,
  orderOf,
  paddingToPct,
  round,
  roundFontSize,
  slideIds,
  slideMap,
  slideOrderOf,
  slidesOf,
  unionBox,
  writeBlocks,
  writeCells,
  writeLabels,
  SLIDE_H,
  SLIDE_W,
  isConnector,
  type Block,
  type Box,
  type ElementSnapshot,
  type NewElement,
  type SlideSnapshot,
} from './model'
import { lineBox, type PresetName } from '../lib/slide-presets'
import type { HistoryEntry } from '../lib/types'
import { emuToPx, type SlidesTemplateLike, type TemplatePlaceholder } from './theme'

// ── slides ──────────────────────────────────────────────────────────────────────────────────────

/** What a layout's text box stands for: the placeholder it was made from (type role + `idx`). */
export interface PlaceholderKey {
  role: 'title' | 'subtitle' | 'body'
  /** `<p:ph idx>` (PowerPoint matches body placeholders on it), null when the template sets none. */
  idx: number | null
}

/** A text box a layout creates, and the placeholder it fills. */
export type LayoutElement = NewElement & { ph?: PlaceholderKey }

/** A layout a new slide can start from: its name and the elements it creates (freely editable after). */
export interface LayoutChoice {
  /** Stored as the slide's `layout`: the template layout's id (`slideLayout7`), a built-in one's type. */
  layout: string
  /** `<p:sldLayout type>` — classifies, never identifies (a corporate template's are all `cust`). */
  type: string
  /** Display name (a template's own, localised by its author). */
  name: string
  elements: LayoutElement[]
  /**
   * The bid Office template it belongs to (AB#445), stored as the slide's `template`; absent = the
   * deck default template (and a built-in layout), which a slide stores as no `template` at all.
   */
  template?: string
  /** The master it belongs to in that template (index); absent for a built-in layout. */
  master?: number
}

/** The same layout of the same template: a layout id is unique inside one template package. */
export function sameLayoutChoice(a: LayoutChoice, b: LayoutChoice): boolean {
  return a.layout === b.layout && (a.template ?? '') === (b.template ?? '')
}

/** px per point on the logical canvas (CSS: 96 dpi / 72), as the backend's seed. */
const PX_PER_PT = 96 / 72

const emptyParagraph = (): Block[] => [{ type: 'p', inlines: [] }]

/** What role a placeholder type plays, or null for the ones a slide does not copy (footer, date, picture…). */
function placeholderRole(type: string): 'title' | 'subtitle' | 'body' | null {
  if (type === 'title' || type === 'ctrTitle') return 'title'
  if (type === 'subTitle') return 'subtitle'
  if (type === 'body' || type === 'obj' || type === '') return 'body'
  return null
}

function placeholderText(p: TemplatePlaceholder, box: Box): LayoutElement | null {
  const role = placeholderRole(p.type)
  if (!role) return null
  const title = role === 'title'
  // The template's text style; what it does not set: the role's defaults (AB#463).
  const fontFamily = p.fontFamily ?? (title ? 'major' : 'minor')
  return {
    kind: 'text',
    // The backend knows a title by the heading font (titleElement); in any other, by this role.
    ...(title && fontFamily !== 'major' ? { role: 'title' as const } : {}),
    ph: { role, idx: typeof p.idx === 'number' ? p.idx : null },
    ...box,
    style: {
      fontFamily,
      fontSize: roundFontSize((p.fontSize ?? (title ? 40 : role === 'subtitle' ? 24 : 20)) * PX_PER_PT),
      color: p.color ?? 'dk1',
      valign: p.valign ?? (title ? 'bottom' : 'top'),
      ...(p.fontWeight ? { fontWeight: p.fontWeight } : {}),
      ...(p.fontStyle ? { fontStyle: p.fontStyle } : {}),
      ...(p.align ? { align: p.align } : {}),
      // The template's insets as % of the placeholder (AB#311); none set: the default padding.
      ...(p.insets && p.box ? { paddingPct: paddingToPct([p.insets.t, p.insets.r, p.insets.b, p.insets.l], p.box.cx, p.box.cy) } : {}),
    },
    text: emptyParagraph(),
  }
}

/** The four layouts a template-less bid offers (boxes as the backend's seed falls back to). */
const BUILT_IN_LAYOUTS: LayoutChoice[] = [
  {
    layout: 'title',
    type: 'title',
    name: 'Title',
    elements: [
      { kind: 'text', ph: { role: 'title', idx: null }, x: 160, y: 224, w: 960, h: 154, style: { fontFamily: 'major', fontSize: 59, color: 'dk1', valign: 'bottom' }, text: emptyParagraph() },
      { kind: 'text', ph: { role: 'subtitle', idx: 1 }, x: 160, y: 404, w: 960, h: 106, style: { fontFamily: 'minor', fontSize: 32, color: 'dk1', valign: 'top' }, text: emptyParagraph() },
    ],
  },
  {
    layout: 'obj',
    type: 'obj',
    name: 'Title and content',
    elements: [
      { kind: 'text', ph: { role: 'title', idx: null }, x: 88, y: 38, w: 1104, h: 139, style: { fontFamily: 'major', fontSize: 44, color: 'dk1', valign: 'middle' }, text: emptyParagraph() },
      { kind: 'text', ph: { role: 'body', idx: 1 }, x: 88, y: 192, w: 1104, h: 457, style: { fontFamily: 'minor', fontSize: 28, color: 'dk1', valign: 'top' }, text: emptyParagraph() },
    ],
  },
  {
    layout: 'titleOnly',
    type: 'titleOnly',
    name: 'Title only',
    elements: [
      { kind: 'text', ph: { role: 'title', idx: null }, x: 88, y: 38, w: 1104, h: 139, style: { fontFamily: 'major', fontSize: 44, color: 'dk1', valign: 'middle' }, text: emptyParagraph() },
    ],
  },
  { layout: 'blank', type: 'blank', name: 'Blank', elements: [] },
]

/**
 * The layouts "+ Slide" offers: every layout of a template's view (one master's), its title /
 * subtitle / body placeholders turned into empty text boxes where the layout puts them (a template
 * only INITIALISES a slide), or four built-in layouts when there is no template. `on` tags each
 * choice with the (template, master) it belongs to (AB#445; slides/deck-templates.ts): no `template`
 * for the deck default template.
 */
export function layoutChoices(tpl: SlidesTemplateLike | null, on: { template?: string; master?: number } = {}): LayoutChoice[] {
  if (!tpl || tpl.layouts.length === 0 || !(tpl.slideSize.cx > 0)) return BUILT_IN_LAYOUTS
  return tpl.layouts.map((l) => ({
    layout: l.id,
    type: l.type,
    name: l.name || l.type,
    elements: l.placeholders.flatMap((p) => {
      if (!p.box) return []
      const el = placeholderText(p, emuToPx(p.box, tpl.slideSize.cx))
      return el ? [{ ...el, x: round(el.x), y: round(el.y), w: round(el.w), h: round(el.h) }] : []
    }),
    ...(on.template ? { template: on.template } : {}),
    ...(on.master !== undefined ? { master: on.master } : {}),
  }))
}

/**
 * Add a slide from a layout (after `index`, else at the end); one transaction. Returns its id. A
 * layout of another template than the deck default puts the slide on that template (AB#445).
 */
export function addSlideFromLayout(
  doc: Y.Doc,
  layout: LayoutChoice,
  index?: number,
  origin: unknown = LOCAL_ORIGIN,
): string {
  const id = newId(doc, 's')
  doc.transact(() => {
    addSlide(
      doc,
      id,
      { layout: layout.layout, ...(layout.template ? { template: layout.template } : {}), ...(index !== undefined ? { index } : {}) },
      origin,
    )
    for (const el of layout.elements) addElement(doc, id, el, undefined, undefined, origin)
  }, origin)
  return id
}

/**
 * A member's slides change for the room's modification history (Y.Array('history')): one readable
 * line about one slide ("Slide 3 layout: Title Slide 1 → Divider 1"), in the `slides` shape the
 * server's slide writes use too. `ai`: the change is the local agent's, made at the member's request.
 */
export function slideHistoryEntry(actor: string, slide: string, label: string, ai?: { prompt?: string }): HistoryEntry {
  return {
    id: crypto.randomUUID(),
    actor,
    // `ai`: an edit the member asked the local agent for (Improve with AI); `prompt` = their instruction.
    kind: ai ? 'ai' : 'human',
    ...(ai?.prompt ? { prompt: ai.prompt } : {}),
    slides: [{ slide, label }],
    createdAt: new Date().toISOString(),
  }
}

/**
 * The layout choice a slide's `layout` names: by id, else (a slide created before ids) by type.
 * `where` (AB#445): only among the choices of the slide's own template (absent = the deck default)
 * and master — a layout id repeats across packages, a type across masters. A built-in choice carries
 * no master and matches any.
 */
export function findLayoutChoice(
  layouts: LayoutChoice[],
  layout: string | undefined,
  where?: { template?: string; master?: number },
): LayoutChoice | null {
  if (!layout) return null
  const own = where
    ? layouts.filter((l) => (l.template ?? '') === (where.template ?? '') && (where.master === undefined || l.master === undefined || l.master === where.master))
    : layouts
  return own.find((l) => l.layout === layout) ?? own.find((l) => l.type === layout) ?? null
}

/** How far (px) an element may sit from a placeholder's box and still be that placeholder. */
const PLACEHOLDER_TOLERANCE = 1

const onBox = (a: Box, b: Box): boolean =>
  Math.abs(a.x - b.x) <= PLACEHOLDER_TOLERANCE &&
  Math.abs(a.y - b.y) <= PLACEHOLDER_TOLERANCE &&
  Math.abs(a.w - b.w) <= PLACEHOLDER_TOLERANCE &&
  Math.abs(a.h - b.h) <= PLACEHOLDER_TOLERANCE

const hasText = (blocks: Block[] | undefined): boolean => {
  const walk = (b: Block): boolean =>
    b.type === 'p' ? b.inlines.some((r) => 'text' in r && r.text.trim() !== '') : b.items.some((item) => item.some(walk))
  return (blocks ?? []).some(walk)
}

/** What a placeholder's text box takes from it on a layout change, unless the member changed it. */
const PLACEHOLDER_STYLE_KEYS = ['fontSize', 'valign', 'fontFamily', 'color', 'fontWeight', 'fontStyle', 'align'] as const

/** The roles a placeholder's content may move to, best first (PowerPoint puts a subtitle in a body and back). */
const COMPATIBLE: Record<PlaceholderKey['role'], Array<PlaceholderKey['role']>> = {
  title: ['title'],
  subtitle: ['subtitle', 'body'],
  body: ['body', 'subtitle'],
}

/**
 * Put a slide on another layout (the toolbar's "Layout" menu), keeping its elements; ONE
 * transaction. The slide stores the new layout's id (the master drawing follows it) and its template
 * (AB#445: set for a layout of another template, removed for one of the deck default template — the
 * slide then follows the deck default), and its placeholder text boxes move to the new layout's
 * matching placeholder:
 * - a text box IS a placeholder of the current layout while it sits on that placeholder's box
 *   (where "New slide" or a previous switch put it); one the member moved or resized is free content;
 * - it moves to the new layout's placeholder of the same role and `idx`, else the first free one of
 *   a compatible role, taking that box and its text style (size, anchor, font, colour, weight, italic,
 *   alignment), each unless the member changed it — a value still the old placeholder's. A value the
 *   new placeholder does not set is cleared, as a new slide would not have it. An old box's stored
 *   `dk1` under a placeholder that now says `lt2` counts as changed, so an existing deck keeps it (AB#463);
 * - an EMPTY placeholder with no match is dropped (nothing to keep, and it would pile up on every
 *   switch); one holding text stays where it is, as everything else on the slide;
 * - a placeholder of the new layout that nothing filled gets its empty text box, as on a new slide.
 */
export function changeSlideLayout(
  doc: Y.Doc,
  slide: SlideSnapshot,
  from: LayoutChoice | null,
  to: LayoutChoice,
  origin: unknown = LOCAL_ORIGIN,
): void {
  const fromSlots = (from?.elements ?? []).filter((e) => e.ph)
  const toSlots = to.elements.filter((e) => e.ph)
  const taken = new Set<number>()
  const filled: Array<{ id: string; slot: LayoutElement }> = []
  for (const id of slide.order) {
    const e = slide.elements[id]
    if (!e || e.kind !== 'text') continue
    const k = fromSlots.findIndex((s, i) => !taken.has(i) && onBox(s, e))
    if (k < 0) continue
    taken.add(k)
    filled.push({ id, slot: fromSlots[k] as LayoutElement })
  }
  const used = new Set<number>()
  const pick = (ph: PlaceholderKey): number => {
    const free = (i: number): boolean => !used.has(i)
    const same = toSlots.findIndex((s, i) => free(i) && s.ph?.role === ph.role && ph.idx !== null && s.ph.idx === ph.idx)
    if (same >= 0) return same
    for (const role of COMPATIBLE[ph.role]) {
      const k = toSlots.findIndex((s, i) => free(i) && s.ph?.role === role)
      if (k >= 0) return k
    }
    return -1
  }
  doc.transact(() => {
    const map = slideMap(doc, slide.id)
    const els = elementsOf(map)
    map.set('layout', to.layout)
    if (to.template) map.set('template', to.template)
    else if (map.has('template')) map.delete('template')
    const drop: string[] = []
    // A slide has one title: the role goes to a title box only when no other box holds it.
    const titled = (except?: string): boolean => [...els.entries()].some(([k, el]) => k !== except && el.get('role') === 'title')
    for (const { id, slot } of filled) {
      const e = slide.elements[id] as ElementSnapshot
      const k = pick(slot.ph as PlaceholderKey)
      if (k < 0) {
        if (!hasText(e.text)) drop.push(id)
        continue
      }
      used.add(k)
      const target = toSlots[k] as LayoutElement
      const m = els.get(id)
      if (!m) continue
      for (const f of ['x', 'y', 'w', 'h'] as const) m.set(f, round(target[f]))
      if (target.role && !titled(id)) m.set('role', target.role)
      const style = m.get('style')
      if (style instanceof Y.Map) {
        for (const key of PLACEHOLDER_STYLE_KEYS) {
          if (e.style[key] !== slot.style?.[key]) continue
          const next = target.style?.[key]
          if (next !== undefined) style.set(key, next)
          else if (style.has(key)) style.delete(key)
        }
      }
    }
    if (drop.length) removeElements(doc, slide.id, drop, origin)
    toSlots.forEach((s, i) => {
      if (used.has(i)) return
      const { ph: _ph, role, ...spec } = s
      addElement(doc, slide.id, role && !titled() ? { ...spec, role } : spec, undefined, undefined, origin)
    })
  }, origin)
}

/** Delete a slide (every place the order lists it, and its map). */
export function deleteSlide(doc: Y.Doc, slideId: string, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const order = slideOrderOf(doc)
    const ids = order.toArray()
    for (let i = ids.length - 1; i >= 0; i--) if (ids[i] === slideId) order.delete(i, 1)
    slidesOf(doc).delete(slideId)
  }, origin)
}

/**
 * Move a slide to `index` in the deck (the index it ends up at). The order holds ids only, so a
 * move is delete + insert of the id and never touches the slide's content: a member editing the
 * slide while another reorders the deck loses nothing.
 */
export function moveSlide(doc: Y.Doc, slideId: string, index: number, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const order = slideOrderOf(doc)
    const ids = order.toArray()
    for (let i = ids.length - 1; i >= 0; i--) if (ids[i] === slideId) order.delete(i, 1)
    order.insert(Math.max(0, Math.min(index, order.length)), [slideId])
  }, origin)
}

/**
 * Move several slides at once (AB#472): `next` is the deck's order after the move. As for
 * {@link moveSlide}, only the moved ids are deleted and re-inserted, in one transaction, so the
 * slides' content is untouched and a concurrent insert by another member keeps its place.
 */
export function placeSlides(doc: Y.Doc, next: readonly string[], moved: readonly string[], origin: unknown = LOCAL_ORIGIN): void {
  const deck = slideIds(doc)
  if (next.length === deck.length && next.every((id, i) => id === deck[i])) return
  doc.transact(() => {
    const order = slideOrderOf(doc)
    const ids = order.toArray()
    for (let i = ids.length - 1; i >= 0; i--) if (moved.includes(ids[i] as string)) order.delete(i, 1)
    next.forEach((id, i) => {
      if (moved.includes(id)) order.insert(Math.min(i, order.length), [id])
    })
  }, origin)
}

// ── z-order ─────────────────────────────────────────────────────────────────────────────────────

export type ZMove = 'front' | 'forward' | 'backward' | 'back'

/** The list after a z-order move of `sel` (ids of that list); the others keep their relative order. */
export function zOrdered(list: string[], sel: string[], how: ZMove): string[] {
  const picked = list.filter((id) => sel.includes(id))
  const rest = list.filter((id) => !sel.includes(id))
  if (picked.length === 0) return list
  if (how === 'front') return [...rest, ...picked]
  if (how === 'back') return [...picked, ...rest]
  const next = [...list]
  if (how === 'forward') {
    for (let i = next.length - 2; i >= 0; i--) {
      if (sel.includes(next[i] as string) && !sel.includes(next[i + 1] as string)) {
        ;[next[i], next[i + 1]] = [next[i + 1] as string, next[i] as string]
      }
    }
  } else {
    for (let i = 1; i < next.length; i++) {
      if (sel.includes(next[i] as string) && !sel.includes(next[i - 1] as string)) {
        ;[next[i], next[i - 1]] = [next[i - 1] as string, next[i] as string]
      }
    }
  }
  return next
}

/**
 * Bring to front / forward, send backward / to back, within the selection's own list (the slide's
 * order, or its group's children). Only the moved ids are deleted and re-inserted, so a concurrent
 * insert by another member keeps its place.
 */
export function reorderZ(doc: Y.Doc, s: SlideSnapshot, ids: string[], how: ZMove, origin: unknown = LOCAL_ORIGIN): void {
  if (ids.length === 0) return
  const parent = parentsOf(s).get(ids[0] as string) ?? null
  if (ids.some((id) => (parentsOf(s).get(id) ?? null) !== parent)) return
  doc.transact(() => {
    const slide = slideMap(doc, s.id)
    const list = parent === null ? orderOf(slide) : childrenOf(elementsOf(slide).get(parent) as Y.Map<unknown>)
    if (!list) return
    const current = list.toArray()
    const next = zOrdered(current, ids, how)
    if (next.every((id, i) => id === current[i])) return
    for (let i = current.length - 1; i >= 0; i--) if (ids.includes(current[i] as string)) list.delete(i, 1)
    next.forEach((id, i) => {
      if (ids.includes(id)) list.insert(Math.min(i, list.length), [id])
    })
  }, origin)
}

// ── align / distribute ──────────────────────────────────────────────────────────────────────────

export type AlignEdge = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'
export type Move = { id: string; dx: number; dy: number }

/**
 * Align: one element aligns to the slide, several to the union of their boxes (PowerPoint's
 * "Align Selected Objects"). Returns the moves for `translate`, without the no-ops.
 */
export function alignMoves(s: SlideSnapshot, ids: string[], edge: AlignEdge): Move[] {
  const boxes = ids.flatMap((id) => (s.elements[id] ? [s.elements[id] as ElementSnapshot] : []))
  if (boxes.length === 0) return []
  const ref: Box = boxes.length === 1 ? { x: 0, y: 0, w: SLIDE_W, h: SLIDE_H } : unionBox(boxes)
  return boxes
    .map((b) => {
      let dx = 0
      let dy = 0
      if (edge === 'left') dx = ref.x - b.x
      else if (edge === 'center') dx = ref.x + ref.w / 2 - (b.x + b.w / 2)
      else if (edge === 'right') dx = ref.x + ref.w - (b.x + b.w)
      else if (edge === 'top') dy = ref.y - b.y
      else if (edge === 'middle') dy = ref.y + ref.h / 2 - (b.y + b.h / 2)
      else dy = ref.y + ref.h - (b.y + b.h)
      return { id: b.id, dx: round(dx), dy: round(dy) }
    })
    .filter((m) => m.dx !== 0 || m.dy !== 0)
}

/**
 * Distribute 3+ elements so the gaps between them are equal, the two outermost staying put
 * (PowerPoint's "Distribute Horizontally / Vertically").
 */
export function distributeMoves(s: SlideSnapshot, ids: string[], axis: 'h' | 'v'): Move[] {
  const boxes = ids.flatMap((id) => (s.elements[id] ? [s.elements[id] as ElementSnapshot] : []))
  if (boxes.length < 3) return []
  const pos = (b: Box): number => (axis === 'h' ? b.x : b.y)
  const size = (b: Box): number => (axis === 'h' ? b.w : b.h)
  const sorted = [...boxes].sort((a, b) => pos(a) + size(a) / 2 - (pos(b) + size(b) / 2))
  const first = sorted[0] as ElementSnapshot
  const last = sorted[sorted.length - 1] as ElementSnapshot
  const span = pos(last) + size(last) - pos(first)
  const gap = (span - sorted.reduce((n, b) => n + size(b), 0)) / (sorted.length - 1)
  const moves: Move[] = []
  let at = pos(first)
  for (const b of sorted) {
    const d = round(at - pos(b))
    if (d !== 0) moves.push({ id: b.id, dx: axis === 'h' ? d : 0, dy: axis === 'v' ? d : 0 })
    at += size(b) + gap
  }
  return moves
}

/** Apply align / distribute moves (one transaction, one undo step). */
export function applyMoves(doc: Y.Doc, slideId: string, moves: Move[], origin: unknown = LOCAL_ORIGIN): void {
  if (moves.length) translate(doc, slideId, moves, origin)
}

// ── copy / paste ────────────────────────────────────────────────────────────────────────────────

/** What Copy keeps: the selected elements and everything nested in them, as plain data. */
export interface Clip {
  roots: string[]
  elements: Record<string, ElementSnapshot>
}

/**
 * Copy the selection (roots in z-order, back to front). Null when nothing is selected. A connector
 * keeps the bindings to elements copied with it (the paste re-binds them to the copies); an end
 * glued to anything else is freed where it is now, so a connector copied alone pastes free.
 */
export function copyElements(s: SlideSnapshot, ids: string[]): Clip | null {
  const parents = parentsOf(s)
  const list = (() => {
    const parent = ids.length ? parents.get(ids[0] as string) ?? null : null
    return parent === null ? s.order : s.elements[parent]?.children ?? []
  })()
  const roots = list.filter((id) => ids.includes(id))
  if (roots.length === 0) return null
  const elements: Record<string, ElementSnapshot> = {}
  for (const r of roots) for (const id of [r, ...descendantsOf(s, r)]) elements[id] = structuredClone(s.elements[id] as ElementSnapshot)
  for (const e of Object.values(elements)) {
    if (!isConnector(e) || (!e.from && !e.to)) continue
    const { start, end } = connectorEnds(s, s.elements[e.id] as ElementSnapshot)
    const kept = unbindEnds(s, e, (b) => !(b.el in elements))
    Object.assign(e, kept?.box ?? lineBox(start, end))
    for (const which of ['from', 'to'] as const) if (kept && !kept[which]) delete e[which]
  }
  return { roots, elements }
}

/** A leaf snapshot → the spec `buildElement` takes. `keepRole`: it lands on a slide of its own (a duplicate). */
function specOf(e: ElementSnapshot, dx: number, dy: number, keepRole: boolean): NewElement {
  const spec: NewElement = {
    kind: e.kind as NewElement['kind'],
    x: round(e.x + dx),
    y: round(e.y + dy),
    w: e.w,
    h: e.h,
    rot: e.rot,
    style: e.style,
  }
  if (keepRole && e.role) spec.role = e.role
  for (const f of ['geom', 'src', 'alt', 'icon'] as const) if (e[f] !== undefined) spec[f] = e[f]
  if (e.adj !== undefined) spec.adj = e.adj
  if (e.lockRatio === false) spec.lockRatio = false
  if (e.crop) spec.crop = { ...e.crop }
  if (e.kind === 'table') spec.header = e.header === true
  if (e.kind === 'table' && e.cols) spec.cols = e.cols
  if (e.kind === 'table' && e.rows) spec.rows = e.rows
  if (e.kind === 'table' && e.cellStyles) spec.cellStyles = e.cellStyles
  if (e.link) spec.link = { ...e.link }
  if (e.anchor) spec.anchor = { ...e.anchor }
  if (e.labels?.length) spec.labels = e.labels
  return spec
}

/**
 * The clip's elements without the anchors a paste may not copy (AB#350): an anchor id is unique, so
 * every anchor (on a whole element, or on letters of its text, cells or labels) whose id the deck
 * already holds is dropped — a duplicate, a copy pasted while its original is still there, a slide
 * duplicated. A cut pasted back keeps them (the original is gone). `keep` false drops them all (a
 * paste into another deck of the artefact, which this one cannot see).
 */
export function pastableElements(doc: Y.Doc, clip: Clip, keep = true): Record<string, ElementSnapshot> {
  const taken = new Set(keep ? deckAnchors(doc).map((a) => a.id) : [])
  const drop = (id: string): boolean => !keep || taken.has(id)
  const out: Record<string, ElementSnapshot> = {}
  for (const [k, src] of Object.entries(clip.elements)) {
    const e = { ...src }
    if (e.anchor && drop(e.anchor.id)) delete e.anchor
    if (e.text) e.text = dropRunAnchors(e.text, drop)
    if (e.cells) e.cells = e.cells.map((row) => row.map((cell) => dropRunAnchors(cell, drop)))
    if (e.labels) e.labels = e.labels.map((l) => ({ ...l, text: dropRunAnchors(l.text, drop) }))
    out[k] = e
  }
  return out
}

/**
 * Paste a clip on top of the slide, offset by (dx, dy), with fresh ids (a group keeps its members,
 * nested groups included); a connector's bindings follow its ends to their copies. One transaction.
 * Returns the new root ids, for the selection. Anchors: {@link pastableElements} (`keepAnchors`).
 * `keepRole`: a copy stays the title; only for a whole slide, as a slide has one title.
 */
export function pasteClip(
  doc: Y.Doc,
  slideId: string,
  clip: Clip,
  dx: number,
  dy: number,
  origin: unknown = LOCAL_ORIGIN,
  keepAnchors = true,
  keepRole = false,
): string[] {
  const roots: string[] = []
  doc.transact(() => {
    const slide = slideMap(doc, slideId)
    const els = elementsOf(slide)
    const copies = new Map<string, string>()
    const elements = pastableElements(doc, clip, keepAnchors)
    const create = (oldId: string): string | null => {
      const e = elements[oldId]
      if (!e) return null
      if (e.kind === 'group') {
        const kids = (e.children ?? []).map(create).filter((k): k is string => k !== null)
        if (kids.length === 0) return null
        const id = newId(doc, 'g')
        const g = new Y.Map<unknown>()
        g.set('kind', 'group')
        g.set('rot', e.rot)
        g.set('style', new Y.Map())
        g.set('children', Y.Array.from(kids))
        els.set(id, g)
        for (const k of ['x', 'y'] as const) g.set(k, round(e[k] + (k === 'x' ? dx : dy)))
        g.set('w', e.w)
        g.set('h', e.h)
        if (e.link) g.set('link', { ...e.link })
        if (e.anchor) g.set('anchor', { ...e.anchor })
        return id
      }
      const id = newId(doc, 'e')
      copies.set(oldId, id)
      const el = buildElement(specOf(e, dx, dy, keepRole))
      els.set(id, el)
      const frag = el.get('text')
      if (frag instanceof Y.XmlFragment && e.text?.length) writeBlocks(frag, e.text)
      if (e.kind === 'table') writeCells(el, e.cells ?? [])
      if (e.labels?.length) writeLabels(el, e.labels)
      return id
    }
    for (const r of clip.roots) {
      const id = create(r)
      if (id) roots.push(id)
    }
    // Bindings once every copy exists: an end glued to an element of the clip glues to its copy.
    for (const [oldId, id] of copies) {
      const e = clip.elements[oldId] as ElementSnapshot
      for (const which of ['from', 'to'] as const) {
        const b = e[which]
        const to = b ? copies.get(b.el) : undefined
        if (b && to) els.get(id)?.set(which, { el: to, site: b.site })
      }
    }
    orderOf(slide).push(roots)
  }, origin)
  return roots
}

/** What Copy keeps of a slide (the sorter's Ctrl+C): its layout, notes and every element. */
export interface SlideClip {
  layout?: string
  notes: string
  clip: Clip | null
}

export function copySlide(doc: Y.Doc, s: SlideSnapshot): SlideClip {
  const notes = slideMap(doc, s.id).get('notes')
  return { ...(s.layout ? { layout: s.layout } : {}), notes: notes instanceof Y.Text ? notes.toString() : '', clip: copyElements(s, s.order) }
}

/** Paste a copied slide as a new slide at `index` (a duplicate, fresh ids; anchors as {@link pasteClip}).
 *  One transaction; returns its id. */
export function pasteSlide(doc: Y.Doc, sc: SlideClip, index: number, origin: unknown = LOCAL_ORIGIN, keepAnchors = true): string {
  let id = ''
  doc.transact(() => {
    id = addSlide(doc, undefined, { ...(sc.layout ? { layout: sc.layout } : {}), index }, origin)
    const notes = slideMap(doc, id).get('notes')
    if (sc.notes && notes instanceof Y.Text) notes.insert(0, sc.notes)
    if (sc.clip) pasteClip(doc, id, sc.clip, 0, 0, origin, keepAnchors, true)
  }, origin)
  return id
}

// ── selection helpers ───────────────────────────────────────────────────────────────────────────

/** The leaves under a selection (a group's members at any depth): what a colour applies to. */
export function leavesOf(s: SlideSnapshot, ids: string[]): ElementSnapshot[] {
  const out: ElementSnapshot[] = []
  for (const id of ids) {
    for (const d of [id, ...descendantsOf(s, id)]) {
      const e = s.elements[d]
      if (e && e.kind !== 'group' && !out.includes(e)) out.push(e)
    }
  }
  return out
}

/** Remove style keys from several elements (one transaction): "No fill" deletes `fill`. */
export function clearStyle(
  doc: Y.Doc,
  slideId: string,
  ids: string[],
  keys: Array<keyof ElementSnapshot['style']>,
  origin: unknown = LOCAL_ORIGIN,
): void {
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const style = els.get(id)?.get('style')
      if (style instanceof Y.Map) for (const k of keys) if (style.has(k)) style.delete(k)
    }
  }, origin)
}

// ── insert ──────────────────────────────────────────────────────────────────────────────────────

/** The nine presets of the restricted HTML, in the order the insert menu lists them. */
export const SHAPE_PRESETS: PresetName[] = ['rect', 'roundRect', 'ellipse', 'triangle', 'diamond', 'chevron', 'homePlate', 'arrow', 'line']

/** A new text box, centred on the slide (nudged by `n` so repeated inserts do not stack exactly).
 *  Each line of `text` is a paragraph (pasted text), and the box is as tall as its lines. */
export function newTextBox(text: string, n = 0): NewElement {
  const off = (n % 6) * 24
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  return {
    kind: 'text',
    x: 380 + off,
    y: 320 + off,
    w: 520,
    h: 80 + (lines.length - 1) * 34,
    style: { fontFamily: 'minor', fontSize: 28, color: 'dk1' },
    text: lines.map((line) => ({ type: 'p' as const, inlines: line ? [{ text: line }] : [] })),
  }
}

/** A new arrow: the line preset with an arrow at its end (the Shape menu's "Arrow line"). */
export function newLineArrow(n = 0): NewElement {
  const line = newShape('line', n)
  return { ...line, style: { ...line.style, lineTail: 'arrow' } }
}

/** A new preset shape, filled with accent1 (a line: stroked, unfilled). */
export function newShape(geom: PresetName, n = 0): NewElement {
  const off = (n % 6) * 24
  if (geom === 'line') {
    return { kind: 'shape', geom, x: 520 + off, y: 300 + off, w: 240, h: 120, style: { line: 'dk1', lineWidth: 3 } }
  }
  const wide = geom === 'chevron' || geom === 'homePlate' || geom === 'arrow'
  return {
    kind: 'shape',
    geom,
    x: 520 + off,
    y: 260 + off,
    w: wide ? 280 : 200,
    h: wide ? 120 : 200,
    style: { fill: 'accent1', color: 'lt1', fontFamily: 'minor', fontSize: 20, align: 'center', valign: 'middle', ...(geom === 'roundRect' ? { radius: 16 } : {}) },
    text: emptyParagraph(),
  }
}

/** The longer side of a newly inserted icon (px). */
export const ICON_SIZE = 96

/**
 * A new Capgemini icon in accent1, its box shaped like the icon (`viewBox`, when the picker has
 * its SVG; else square) with a longer side of ICON_SIZE, near the slide's centre.
 */
export function newIcon(icon: string, n = 0, viewBox?: string): NewElement {
  const off = (n % 6) * 24
  const [, , vw = 0, vh = 0] = (viewBox ?? '').split(' ').map(Number)
  const k = vw > 0 && vh > 0 ? ICON_SIZE / Math.max(vw, vh) : 0
  const w = k ? round(vw * k) : ICON_SIZE
  const h = k ? round(vh * k) : ICON_SIZE
  return { kind: 'icon', icon, x: round(SLIDE_W / 2 - w / 2 + off), y: round(SLIDE_H / 2 - h / 2 + off), w, h, style: { color: 'accent1' } }
}

/** "Change icon…": point icon elements at another library icon (box, colour and opacity kept). */
export function setIcon(doc: Y.Doc, slideId: string, ids: string[], icon: string, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const el = els.get(id)
      if (el?.get('kind') === 'icon' && el.get('icon') !== icon) el.set('icon', icon)
    }
  }, origin)
}

/** Point an image element at a blob (an empty image placeholder receiving the picked picture). */
export function setImageSource(doc: Y.Doc, slideId: string, id: string, src: string, alt: string, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    if (el?.get('kind') !== 'image') return
    el.set('src', src)
    if (alt) el.set('alt', alt)
  }, origin)
}

// ── recently used custom colours ────────────────────────────────────────────────────────────────

/** How many custom colours the Format panel remembers, per part. */
export const RECENT_COLOURS = 8

/**
 * The part's recently used custom colours (`#RRGGBB`, latest first), kept in the part's own Y.Doc
 * (PowerPoint keeps them in the file too, `p:clrMru`): every member of the part sees the same
 * list. Outside the slides and the undo manager's scope, so Ctrl+Z never undoes a remembered colour.
 */
export const recentColoursOf = (doc: Y.Doc): Y.Array<string> => doc.getArray<string>('recentColors')

/** The list, read tolerantly: valid colours, once each, at most RECENT_COLOURS. */
export function recentColours(doc: Y.Doc): string[] {
  const out: string[] = []
  for (const c of recentColoursOf(doc).toArray()) {
    const v = typeof c === 'string' ? c.toUpperCase() : ''
    if (/^#[0-9A-F]{6}$/.test(v) && !out.includes(v)) out.push(v)
    if (out.length === RECENT_COLOURS) break
  }
  return out
}

/** Remember a custom colour: moved to the front, the list capped. A theme slot is not remembered. */
export function rememberColour(doc: Y.Doc, colour: string): void {
  const v = colour.toUpperCase()
  if (!/^#[0-9A-F]{6}$/.test(v)) return
  doc.transact(() => {
    const list = recentColoursOf(doc)
    const items = list.toArray()
    for (let i = items.length - 1; i >= 0; i--) if (String(items[i]).toUpperCase() === v) list.delete(i, 1)
    list.insert(0, [v])
    if (list.length > RECENT_COLOURS) list.delete(RECENT_COLOURS, list.length - RECENT_COLOURS)
  })
}
